import type { FC } from 'react';
import { Button, DatePicker, Dropdown, Flex, Input, Select } from 'antd';
import { FilterOutlined, SearchOutlined } from '@ant-design/icons';
import type { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import {
  ANOMALY_SESSION_QUALIFICATION_VALUES,
  SESSION_TABLE_STATUS_VALUES,
  sessionTableStatusLabelKey,
} from '@evimo/types/constants/session/session-table-status';
import { qualificationLabelKey } from '~/app/components/session/utils/qualificationDisplay';
import { FilterBar } from '~/app/components/common/FilterBar/FilterBar';

export interface SessionFiltersProps {
  range: [Dayjs, Dayjs] | null;
  onRangeChange: (range: [Dayjs, Dayjs] | null) => void;
  siteName?: string;
  sites: string[];
  onSiteChange: (siteName?: string) => void;
  onSiteSearch: (value: string) => void;
  chargerName?: string;
  chargers: string[];
  onChargerChange: (chargerName?: string) => void;
  onChargerSearch: (value: string) => void;
  connectorName?: string;
  connectors: string[];
  onConnectorChange: (connectorName?: string) => void;
  onConnectorSearch: (value: string) => void;
  sessionStatus?: string;
  onSessionStatusChange: (sessionStatus?: string) => void;
  anomalyQualification?: string;
  onAnomalyQualificationChange: (anomalyQualification?: string) => void;
  search: string;
  onSearchChange: (search: string) => void;
}

function toOptions(values: string[]) {
  return values.map((value) => ({ value, label: value }));
}

export const SessionFilters: FC<SessionFiltersProps> = ({
  range,
  onRangeChange,
  siteName,
  sites,
  onSiteChange,
  onSiteSearch,
  chargerName,
  chargers,
  onChargerChange,
  onChargerSearch,
  connectorName,
  connectors,
  onConnectorChange,
  onConnectorSearch,
  sessionStatus,
  onSessionStatusChange,
  anomalyQualification,
  onAnomalyQualificationChange,
  search,
  onSearchChange,
}) => {
  const { t } = useTranslation('sessions');

  const sessionStatusOptions = SESSION_TABLE_STATUS_VALUES.map((value) => ({
    value,
    label: t(`end.${sessionTableStatusLabelKey(value)}`),
  }));

  const anomalyOptions = ANOMALY_SESSION_QUALIFICATION_VALUES.map((value) => ({
    value,
    label: t(`qualification.${qualificationLabelKey(value)}`),
  }));

  return (
    <FilterBar>
      <Input
        allowClear
        className="evimo-filter-search"
        prefix={<SearchOutlined />}
        placeholder={t('filters.search')}
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
      />
      <DatePicker.RangePicker
        value={range}
        onChange={(value) =>
          onRangeChange(value?.[0] && value[1] ? [value[0], value[1]] : null)
        }
        placeholder={[t('filters.from'), t('filters.to')]}
      />
      <Dropdown
        trigger={['click']}
        dropdownRender={() => (
          <div className="evimo-filter-panel">
            <Flex vertical gap={12}>
              <div className="evimo-filter-panel__field">
                <span className="evimo-filter-panel__label">
                  {t('columns.status')}
                </span>
                <Select
                  allowClear
                  placeholder={t('filters.selectSessionStatus')}
                  value={sessionStatus}
                  options={sessionStatusOptions}
                  onChange={(value) =>
                    onSessionStatusChange(value ?? undefined)
                  }
                  aria-label={t('columns.status')}
                />
              </div>
              <div className="evimo-filter-panel__field">
                <span className="evimo-filter-panel__label">
                  {t('columns.anomalyDetected')}
                </span>
                <Select
                  allowClear
                  placeholder={t('filters.selectAnomalyDetected')}
                  value={anomalyQualification}
                  options={anomalyOptions}
                  onChange={(value) =>
                    onAnomalyQualificationChange(value ?? undefined)
                  }
                  aria-label={t('columns.anomalyDetected')}
                />
              </div>
              <div className="evimo-filter-panel__field">
                <span className="evimo-filter-panel__label">
                  {t('filters.site')}
                </span>
                <Select
                  allowClear
                  showSearch
                  filterOption={false}
                  placeholder={t('filters.selectSite')}
                  value={siteName}
                  options={toOptions(sites)}
                  onSearch={onSiteSearch}
                  onChange={(value) => onSiteChange(value ?? undefined)}
                />
              </div>
              <div className="evimo-filter-panel__field">
                <span className="evimo-filter-panel__label">
                  {t('filters.asset')}
                </span>
                <Select
                  allowClear
                  showSearch
                  filterOption={false}
                  placeholder={t('filters.selectAsset')}
                  value={chargerName}
                  options={toOptions(chargers)}
                  onSearch={onChargerSearch}
                  onChange={(value) => onChargerChange(value ?? undefined)}
                />
              </div>
              <div className="evimo-filter-panel__field">
                <span className="evimo-filter-panel__label">
                  {t('filters.component')}
                </span>
                <Select
                  allowClear
                  showSearch
                  filterOption={false}
                  disabled={!chargerName}
                  placeholder={
                    chargerName
                      ? t('filters.selectComponent')
                      : t('filters.selectAssetFirst')
                  }
                  value={connectorName}
                  options={toOptions(connectors)}
                  onSearch={onConnectorSearch}
                  onChange={(value) => onConnectorChange(value ?? undefined)}
                />
              </div>
            </Flex>
          </div>
        )}
      >
        <Button
          type="default"
          icon={<FilterOutlined />}
          aria-label={t('filters.filters', 'Filters')}
        />
      </Dropdown>
    </FilterBar>
  );
};
